import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Users, Pencil, Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { LeadContact } from "@shared/schema";

type ContactForm = { id?: string; name: string; title: string; phone: string; email: string };
const BLANK: ContactForm = { name: "", title: "", phone: "", email: "" };

function ContactInlineForm({
  form, onChange, onSave, onCancel, isSaving,
}: {
  form: ContactForm;
  onChange: (f: ContactForm) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="space-y-2 py-2 border rounded-lg p-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-muted-foreground">Name *</Label>
          <Input
            className="h-7 text-xs"
            value={form.name}
            onChange={e => onChange({ ...form, name: e.target.value })}
            placeholder="Full name"
            data-testid="input-lead-contact-name"
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Title</Label>
          <Input
            className="h-7 text-xs"
            value={form.title}
            onChange={e => onChange({ ...form, title: e.target.value })}
            placeholder="Title / role"
            data-testid="input-lead-contact-title"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-muted-foreground">Phone</Label>
          <Input
            className="h-7 text-xs"
            value={form.phone}
            onChange={e => onChange({ ...form, phone: e.target.value })}
            placeholder="+1 (555) 000-0000"
            data-testid="input-lead-contact-phone"
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Email</Label>
          <Input
            className="h-7 text-xs"
            value={form.email}
            onChange={e => onChange({ ...form, email: e.target.value })}
            placeholder="email@example.com"
            data-testid="input-lead-contact-email"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!form.name.trim() || isSaving}
          className="text-xs font-medium text-primary hover:underline disabled:opacity-40"
          data-testid="button-save-lead-contact"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="button-cancel-lead-contact"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function LeadContactsSection({ leadId }: { leadId?: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<ContactForm | null>(null);

  const { data: contacts = [], isLoading } = useQuery<LeadContact[]>({
    queryKey: ["/api/leads", leadId, "contacts"],
    queryFn: () => apiRequest("GET", `/api/leads/${leadId}/contacts`).then(r => r.json()),
    enabled: !!leadId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["/api/leads", leadId, "contacts"] });

  const createMutation = useMutation({
    mutationFn: (data: Omit<ContactForm, "id">) =>
      apiRequest("POST", `/api/leads/${leadId}/contacts`, data),
    onSuccess: () => { invalidate(); setForm(null); },
    onError: () => toast({ title: "Error", description: "Failed to save contact", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: ContactForm) =>
      apiRequest("PATCH", `/api/leads/${leadId}/contacts/${id}`, data),
    onSuccess: () => { invalidate(); setForm(null); },
    onError: () => toast({ title: "Error", description: "Failed to update contact", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/leads/${leadId}/contacts/${id}`),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: "Error", description: "Failed to delete contact", variant: "destructive" }),
  });

  const handleSave = () => {
    if (!form || !form.name.trim()) return;
    if (form.id) {
      updateMutation.mutate(form);
    } else {
      const { id: _id, ...data } = form;
      createMutation.mutate(data);
    }
  };

  if (!leadId) return null;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-toggle-lead-contacts"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <Users className="w-3 h-3" />
          <span>
            {contacts.length > 0 ? `Additional contacts (${contacts.length})` : "Additional contacts"}
          </span>
        </button>
        {!form && (
          <button
            type="button"
            onClick={() => { setExpanded(true); setForm(BLANK); }}
            className="text-xs text-primary hover:underline font-medium"
            data-testid="button-add-lead-contact"
          >
            add contact +
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-1 pl-2 border-l-2 border-muted ml-1">
          {isLoading && <p className="text-xs text-muted-foreground py-1">Loading…</p>}
          {!isLoading && contacts.length === 0 && !form && (
            <p className="text-xs text-muted-foreground py-1 italic">No additional contacts yet.</p>
          )}

          {contacts.map((c) => (
            <div key={c.id}>
              {form?.id === c.id ? (
                <ContactInlineForm
                  form={form}
                  onChange={setForm}
                  onSave={handleSave}
                  onCancel={() => setForm(null)}
                  isSaving={isSaving}
                />
              ) : (
                <div className="flex items-start justify-between group py-1" data-testid={`row-lead-contact-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{c.name}</span>
                    {c.isPrimary && (
                      <sup
                        className="text-[9px] leading-none text-muted-foreground/70 ml-0.5 cursor-help select-none"
                        title="Primary AI-discovered contact"
                        data-testid={`primary-flag-lead-contact-${c.id}`}
                      >
                        ★
                      </sup>
                    )}
                    {c.title && <span className="text-xs text-muted-foreground"> · {c.title}</span>}
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="hover:underline md:pointer-events-none md:cursor-default">
                          {c.phone}
                        </a>
                      )}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setForm({ id: c.id, name: c.name, title: c.title || "", phone: c.phone || "", email: c.email || "" })}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      data-testid={`button-edit-lead-contact-${c.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(c.id)}
                      className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-0.5"
                      data-testid={`button-delete-lead-contact-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {form && !form.id && (
            <ContactInlineForm
              form={form}
              onChange={setForm}
              onSave={handleSave}
              onCancel={() => setForm(null)}
              isSaving={isSaving}
            />
          )}
          {!form && (
            <button
              type="button"
              onClick={() => setForm(BLANK)}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 pt-1"
              data-testid="button-add-another-lead-contact"
            >
              <Plus className="w-3 h-3" /> Add contact
            </button>
          )}
        </div>
      )}
    </div>
  );
}
